import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const teeButtonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-[var(--spacing-xs)] font-medium transition-all duration-200 focus-visible:outline-none disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--color-forest)] text-[var(--text-on-accent)] hover:bg-[var(--color-forest-deep)] disabled:bg-[var(--background-input-field-disabled)] disabled:text-[var(--text-Label-disabled)]',
        secondary:
          'bg-[var(--background-card)] text-[var(--text-content-title)] border border-[var(--border-default)] hover:bg-[var(--background-card-elevated)] hover:border-[var(--color-forest)] disabled:bg-transparent disabled:border-[var(--border-default)] disabled:text-[var(--text-Label-disabled)]',
        tertiary:
          'bg-transparent text-[var(--text-content-sub-title)] hover:text-[var(--text-content-title)] disabled:text-[var(--text-Label-disabled)]',
        ghost:
          'bg-transparent text-[var(--text-content-sub-title)] hover:text-[var(--text-content-title)] disabled:text-[var(--text-Label-disabled)]',
        destructive:
          'bg-transparent text-[var(--color-maroon)] border border-[rgba(146,52,48,0.25)] hover:bg-[rgba(146,52,48,0.08)] disabled:border-[var(--border-default)] disabled:text-[var(--text-Label-disabled)]',
        link:
          'bg-transparent text-[var(--color-forest)] hover:text-[var(--color-forest-deep)] hover:underline disabled:text-[var(--text-Label-disabled)]',
      },
      size: {
        s: 'max-h-[40px] px-2.5 py-3 gap-1.5 text-body-s font-medium',
        m: 'max-h-[44px] px-4 py-3 gap-1.5 text-body-m font-medium',
        l: 'max-h-[52px] px-5 py-4 gap-2 text-heading-h5 font-medium',
        xl: 'max-h-[60px] px-5 py-4 gap-2 text-heading-h4 font-medium',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 's',
    },
  }
);

const fontFamilyMap: Record<string, string> = {
  primary: 'var(--type-font-family-heading)',
  secondary: 'var(--type-font-family-body)',
  tertiary: 'var(--type-font-family-body)',
  destructive: 'var(--type-font-family-heading)',
  link: 'var(--type-font-family-body)',
};

export interface TeeButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof teeButtonVariants> {
  asChild?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  text?: string | React.ReactNode;
}

const TeeButton = React.forwardRef<HTMLButtonElement, TeeButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      leftIcon,
      rightIcon,
      text,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const fontFamily = variant ? fontFamilyMap[variant] : fontFamilyMap.primary;
    const content = text !== undefined ? text : children;

    return (
      <Comp
        className={cn(teeButtonVariants({ variant, size, className }))}
        style={{
          fontFamily,
          ...style,
        }}
        ref={ref}
        {...props}
      >
        {leftIcon && <span className="flex items-center">{leftIcon}</span>}
        {content}
        {rightIcon && <span className="flex items-center">{rightIcon}</span>}
      </Comp>
    );
  }
);
TeeButton.displayName = 'TeeButton';

export { TeeButton, teeButtonVariants };
